import React from 'react';
import { useIntl } from 'react-intl';

import SendReminder from '~/components/Shared/SendReminder/SendReminder';
import { Icon } from '~/eds/src/components/Icon/Icon';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Text } from '~/eds/src/components/Text/Text';
import { IconColor } from '~/eds/src/types/IconColor';
import { IconType } from '~/eds/src/types/IconType';
import { StatusType } from '~/eds/src/types/StatusType';
import {
  EsignatureRecipient,
  EsignatureSignerStatusValue,
} from '~/features/ticket/types/esignature';
import { TicketUserItem } from '~/features/ticket/user-item/TicketUserItem';

import { useSendReminder } from './useSendReminder';
import { useTicketRecipientAssignment } from './useTicketRecipientAssignment';

const getSignerStatusIcon = (
  statusValue: EsignatureSignerStatusValue,
  isUnassigned: boolean,
): { icon: IconType; iconColor: IconColor } => {
  const statusMap: Record<
    EsignatureSignerStatusValue,
    { icon: IconType; iconColor: IconColor }
  > = {
    [EsignatureSignerStatusValue.SignatureRequestNotSent]: {
      icon: 'status-not-started',
      iconColor: isUnassigned ? 'status.danger' : 'status.warning',
    },
    [EsignatureSignerStatusValue.Pending]: {
      icon: 'status-pending',
      iconColor: 'status.warning',
    },
    [EsignatureSignerStatusValue.Queued]: {
      icon: 'status-not-started',
      iconColor: 'status.warning',
    },
    [EsignatureSignerStatusValue.Signed]: {
      icon: 'status-success',
      iconColor: 'status.success',
    },
    [EsignatureSignerStatusValue.Error]: {
      icon: 'status-reject',
      iconColor: 'status.danger',
    },
    [EsignatureSignerStatusValue.Declined]: {
      icon: 'status-reject',
      iconColor: 'status.danger',
    },
  };

  return (
    statusMap[statusValue] || {
      icon: 'status-not-started',
      iconColor: 'status.warning',
    }
  );
};

interface Props {
  recipient: EsignatureRecipient;
}

export const TicketRecipient = ({ recipient }: Props) => {
  const intl = useIntl();
  const {
    allowInternalReassignment,
    allowInternalAssignment,
    allAssignedSignerIds,
    handleAssignment,
    reassignSignerModal,
  } = useTicketRecipientAssignment(recipient);
  const { showSendReminder, lastReminderDate, sendReminder } =
    useSendReminder(recipient);

  const getChip = ():
    | {
        text: string;
        status: StatusType;
      }
    | undefined => {
    if (recipient.isExternal) {
      return {
        text: intl.formatMessage({
          id: 'TicketRecipients.counterpartySigner',
          defaultMessage: 'Counterparty Signer',
          description: 'Label for counterparty signer on a signing package',
        }),
        status: 'info',
      };
    }
    return undefined;
  };

  const renderStatus = (): React.ReactNode => {
    if (!recipient.status) {
      return null;
    }

    const { value, label } = recipient.status;
    const isUnassigned = !recipient.userId && !recipient.external;
    const { icon, iconColor } = getSignerStatusIcon(value, isUnassigned);

    return (
      <Layout align="center" spacing={1} data-testid="ticket-recipient-status">
        <Icon icon={icon} color={iconColor} size="m" />
        <Text variant="body">{label}</Text>
      </Layout>
    );
  };

  return (
    <>
      <TicketUserItem
        userId={recipient.userId}
        externalUser={recipient.external}
        placeholder={intl.formatMessage({
          id: 'TicketRecipients.unassignedSigner',
          defaultMessage: 'Unassigned Signer',
          description: 'Label for unassigned signer',
        })}
        chip={getChip()}
        actions={{
          reassign: allowInternalReassignment,
          assign: allowInternalAssignment,
        }}
        onAssign={handleAssignment}
        allAssignedUserIds={allAssignedSignerIds}
      >
        {showSendReminder && (
          <SendReminder
            actionHandler={sendReminder}
            lastReminderDate={lastReminderDate?.toISOString() ?? undefined}
          />
        )}
        {renderStatus()}
      </TicketUserItem>
      {reassignSignerModal}
    </>
  );
};
